/* NUMBERS THE SIMULATION OWNS AND THE READOUT HAS TO AGREE WITH.
 *
 * Small on purpose. When `hud.js` came out of `game.js` it took the ember
 * cooldown BAR with it while the cooldown itself stayed behind, and the bar
 * divides by the duration — so `EMBER_COOL` was suddenly undefined inside a
 * drawing function. Nothing failed: the expression only evaluates while the
 * cooldown is actually running, so the whole suite and both rendered test
 * runs went straight past a `ReferenceError` waiting for the first player to
 * use the Emberwake.
 *
 * That is the shape of the risk in splitting a 4,500-line class, and the
 * answer is not to keep the class. It is for a constant that two modules must
 * agree on to live somewhere neither of them owns.
 */

/** Frames the Emberwake needs before it can be used again. `game.js` counts
 *  it down; `hud.js` draws what is left of it. */
export const EMBER_COOL = 150;

// Shared placement/save ceiling bounds world simulation without deleting valid hulls on reload.
export const MAX_HULLS = 128;

/* THE SAME NUMBERS, ON BOTH SIDES OF THE DISK.
 *
 * Everything below was a `const` in `game.ts` until the persistence layer
 * came out into `src/save.ts`, and every one of them is read by both halves:
 * `game.ts` GRANTS the thing, `save.ts` CLAMPS what a save may claim of it.
 * That is precisely the pair this file's opening comment exists for, and
 * leaving them behind would have made `save.ts` import `game.ts` back — a
 * cycle — or, worse, restate the ceiling, which is the defect the comment on
 * `MAX_HP_CAP` was written about.
 *
 * `MAX_HP_CAP` and `MAX_PODS_CAP` are re-exported
 * from `game.ts`, which is where `main.ts` and the suites import them from.
 */

/** How many of each stackable a run may carry. The same numbers the loader
 *  clamps to, named once, so the two cannot disagree. */
/* `MAX_REACH` and `MAX_WARD` are gone with the two fields they capped (2026-09-06): both were the shop's,
 * nothing granted either since the pivot, and a cap on a number nothing writes is furniture. */

// One source of truth for the ceilings, shared by what grants them and what
// validates them. They used to disagree, so a 14-glim purchase past the cap
// was silently deleted by the next reload.
/* CAPS A PLAYER CAN ACTUALLY REACH — IN AN ORDINARY WORLD.
 *
 * `atCap` refuses a purchase that would do nothing, and its own comment says
 * "a cap the player can hit is a rule; a cap that quietly eats the price is a
 * pickpocket". These were 48/40, then 40/40, and BOTH were still pickpockets:
 * counting every source of vigor and pods in 200 worlds — caves, vault
 * plinths, and shop stock including the Glimstone and the Pouch — a cap of 40
 * was reachable in 1 world of 200 for vigor and 16 for pods, and
 * `atCap('glimstone')`, which needs BOTH at once, in 0 of 200.
 *
 * The 40 was picked by a check that asserted against the MAXIMUM of the
 * sample, so it passed on the luckiest seed drawn and said nothing about the
 * world anybody actually gets. That check was mine and an adversarial
 * reviewer caught it; it measures the MEDIAN now, and the joint condition
 * separately, because an AND cannot be seen by two per-axis tests.
 *
 * 26 is that median. It makes the refusal a rule roughly half the time
 * instead of never, and it is the number the design lens recommended.
 */
export const MAX_HP_CAP = 26, MAX_PODS_CAP = 26;
/** How much life the beasts still standing gain when one falls: the three fights were interchangeable (the design lens, 2026-09-05), so the last is the hardest. A record's cap is its kind's life times this to the power of the beasts slain. */
export const BEAST_GROWTH = 1.4;
/* What felling one is worth -- and it is NOT free to change. `stocked` in
 * src/entities.ts adds a point to a boss's committed bite for every 3 vigor
 * the player carries over 14, and 14 is exactly this number twice over the
 * starting 10: the rubber band is calibrated to give a player who has felled
 * two beasts no extra damage at all. Raised to 3 on 2026-09-09 to buy the
 * last fight a margin, it crossed that threshold instead and made things
 * worse -- q0 went from a win in 2.7 minutes to 51 deaths, 49 of them at a
 * beast. The margin belongs in how the fight is fought, not here. */
export const BEAST_VIGOR = 2;
/** Where each Great Beast belongs when the world has room there: its lair is placed in these countries first (LS-239/the lore lens, round 47: the bestiary reads this table, so it cannot lie). */
export const BEAST_HOME: Record<string, string[]> = { sow: ['dust', 'heath'], oldshell: ['scree', 'fen'], bramblemother: ['fen', 'heath'] };

/** How many things a spore nest will ever send before it is spent. */
export const NEST_BROOD = 12;

/** The three tools, in slot order. `game.ts` keeps the flag and the name each
 *  one maps to; what lives here is the list the loader validates `state.tool`
 *  against. */
export const TOOLS = ['pods', 'tide', 'ember'];
